import React, { useEffect, useRef, useState } from 'react';
import { Base64 } from 'js-base64';
import { useHistory, useParams } from 'react-router-dom';
import { FiLock, FiArrowRight, FiEye, FiEyeOff, FiLoader } from 'react-icons/fi';
import logo from '../../../../assets/images/logo/Haylo-for-white-background.png';
import { baseURLPublic } from '../../../../config/BaseUrl';
import './gallery-pin.css';

export default function MicrositeOnePassword() {
  const { id } = useParams();
  const history = useHistory();
  let eventId;
  try { eventId = Base64.atob(id); } catch (_) { eventId = ''; }
  const [pin, setPin] = useState('');
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const input = useRef(null);
  const inFlight = useRef(false);
  const requestVersion = useRef(0);
  useEffect(() => {
    setPin(''); setError(''); setLoading(false); inFlight.current = false;
    return () => { requestVersion.current += 1; };
  }, [id]);
  const updatePin = (value) => { setPin(value.replace(/\D/g, '').slice(0, 4)); setError(''); };
  useEffect(() => { if (error && !loading) input.current?.focus(); }, [error, loading]);

  const checkPassword = async (event) => {
    event.preventDefault();
    if (inFlight.current) return;
    if (!/^\d+$/.test(eventId)) { setError('This gallery link is unavailable.'); return; }
    if (!/^\d{4}$/.test(pin)) { setError('Please enter your 4-digit PIN.'); input.current?.focus(); return; }
    inFlight.current = true; setLoading(true); setError('');
    const version = requestVersion.current;
    try {
      const { data } = await baseURLPublic.post('/check-gallery-password', { password: pin, eventId });
      if (version !== requestVersion.current) return;
      if (data.message === 'success') {
        try { localStorage.setItem('galleryPasswordCheck', true); }
        catch (_) { setError('Please allow browser storage to open this gallery.'); return; }
        history.push(`/social/gallery/${id}`);
      } else {
        setError('That PIN is incorrect. Please try again.');
        setPin(''); input.current?.focus();
      }
    } catch (_) {
      if (version === requestVersion.current) setError('We could not check your PIN. Please try again.');
    } finally {
      if (version === requestVersion.current) { inFlight.current = false; setLoading(false); }
    }
  };

  return <main className="sg-gallery-pin-page">
    <header className="sg-pin-header"><img src={logo} alt="The Haylo" /></header>
    <div className="sg-pin-main">
      <section className="sg-pin-card" aria-labelledby="sg-pin-title">
        <span className="sg-pin-lock" aria-hidden="true"><FiLock /></span>
        <p className="sg-pin-eyebrow">YOUR MOMENTS ARE WAITING</p>
        <h1 id="sg-pin-title">Unlock your gallery</h1>
        <p className="sg-pin-description" id="sg-pin-help">Enter the 4-digit PIN provided by your venue.</p>
        <form onSubmit={checkPassword} aria-busy={loading}>
          <label className="sg-pin-label" htmlFor="sg-gallery-pin">Gallery PIN</label>
          <div className={`sg-pin-entry ${error ? 'sg-pin-entry-error' : ''}`}>
            <input ref={input} id="sg-gallery-pin" className="sg-pin-input" type={visible ? 'text' : 'password'} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{4}" minLength={4} maxLength={4} required value={pin} disabled={loading} aria-invalid={!!error} aria-describedby={`sg-pin-help${error ? ' sg-pin-error' : ''}`} onChange={(event) => updatePin(event.target.value)} onPaste={(event) => { event.preventDefault(); updatePin(event.clipboardData.getData('text')); }} />
            <div className="sg-pin-boxes" aria-hidden="true">{[0, 1, 2, 3].map((index) => <span key={index} className={`${pin[index] ? 'is-filled' : ''} ${index === pin.length ? 'is-current' : ''}`}>{pin[index] ? visible ? pin[index] : '•' : ''}</span>)}</div>
          </div>
          <button type="button" className="sg-pin-visibility" aria-pressed={visible} onClick={() => setVisible(!visible)}>{visible ? <FiEyeOff aria-hidden="true" /> : <FiEye aria-hidden="true" />}{visible ? 'Hide PIN' : 'Show PIN'}</button>
          {error && <p className="sg-pin-error" id="sg-pin-error" role="alert">{error}</p>}
          <button type="submit" className="sg-pin-submit" disabled={loading || pin.length !== 4}>{loading ? <><FiLoader className="sg-pin-spinner" aria-hidden="true" />Checking PIN…</> : <>Open gallery<FiArrowRight aria-hidden="true" /></>}</button>
          <p className="sg-pin-assistance">Need the PIN? Ask a member of the venue team.</p>
        </form>
      </section>
    </div>
    <footer className="sg-pin-footer"><span>Powered by</span><img src={logo} alt="Haylo" /></footer>
  </main>;
}
